import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search, ChevronRight, ChevronDown, MapPin, Star,
  Shield, CreditCard, Heart, Users, Building2, Activity,
  Stethoscope, Eye, Baby, Brain, Bone, Pill, FlaskConical,
  Microscope, Syringe, Scan, Percent, Clock, Tag
} from "lucide-react";
import { useI18n } from "@/contexts/I18nContext";
import { useAuth } from "@/contexts/AuthContext";
import PublicHeader from "@/components/shared/PublicHeader";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger
} from "@/components/ui/accordion";

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_ICONS = {
  "Dental": Stethoscope,
  "Dermatology": Heart,
  "Ophthalmology": Eye,
  "Obstetrics": Baby,
  "Neurology": Brain,
  "Orthopedics": Bone,
  "Pharmacy": Pill,
  "Laboratory": FlaskConical,
  "Diagnostics": Microscope,
  "Radiology": Scan,
  "Pediatrics": Baby,
  "Cardiology": Activity,
  "default": Stethoscope,
};

function getCategoryIcon(name) {
  for (const [key, Icon] of Object.entries(CATEGORY_ICONS)) {
    if (name && name.toLowerCase().includes(key.toLowerCase())) return Icon;
  }
  return CATEGORY_ICONS.default;
}

export default function HomePage() {
  const { lang, isRTL, t } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [governorates, setGovernorates] = useState([]);
  const [neighborhoods, setNeighborhoods] = useState([]);
  const [categories, setCategories] = useState([]);

  // Search state
  const [specialty, setSpecialty] = useState("");
  const [governorate, setGovernorate] = useState("");
  const [city, setCity] = useState("");

  const loadHomepage = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/public/homepage`);
      const json = await res.json();
      setData(json);
    } catch (err) {
      console.error("Failed to load homepage data:", err);
    }
  }, []);

  const loadSpecialties = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/public/specialties`);
      const json = await res.json();
      setCategories(json.items || []);
    } catch (err) {
      console.error("Failed to load specialties:", err);
    }
  }, []);

  const loadGovernorates = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/public/governorates`);
      const json = await res.json();
      setGovernorates(json.items || []);
    } catch (err) {
      console.error("Failed to load governorates:", err);
    }
  }, []);

  useEffect(() => { loadHomepage(); loadSpecialties(); loadGovernorates(); }, [loadHomepage, loadSpecialties, loadGovernorates]);

  useEffect(() => {
    if (!governorate) { setNeighborhoods([]); setCity(""); return; }
    fetch(`${API}/api/public/neighborhoods/${governorate}`)
      .then(r => r.json())
      .then(json => setNeighborhoods(json.items || []))
      .catch(() => setNeighborhoods([]));
  }, [governorate]);

  const handleSearch = () => {
    const params = new URLSearchParams();
    if (specialty) params.set("specialty", specialty);
    if (governorate) params.set("governorate", governorate);
    if (city) params.set("city", city);
    navigate(`/search?${params.toString()}`);
  };

  const stats = data?.stats || { providers: 0, specialties: 0, active_offers: 0 };

  return (
    <div className="min-h-screen bg-background" dir={isRTL ? "rtl" : "ltr"}>
      {/* Header */}
      <PublicHeader />

      {/* Hero Section */}
      <section
        className="relative min-h-[90vh] flex flex-col"
        style={{ backgroundImage: "url(https://hemaia.net/images/hero.jpg)", backgroundSize: "cover", backgroundPosition: "center top" }}
        data-testid="hero-section"
      >
        {/* Overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/75" />

        {/* Centered content */}
        <div className="relative flex-1 flex flex-col items-center justify-center text-center px-4 pt-24 pb-40">
          <p className="text-white/70 text-xs font-bold uppercase tracking-[0.25em] mb-4" data-testid="hero-eyebrow">
            {lang === "ar" ? "عضويتك الطبية المميزة في مصر" : "Egypt's Premium Healthcare Membership"}
          </p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-white leading-[1.1] mb-5">
            {lang === "ar" ? (
              <>استمتع برعاية صحية أفضل،<br /><span className="text-sky-400">في كل مرة.</span></>
            ) : (
              <>Access Better Healthcare,<br /><span className="text-sky-400">Every Time.</span></>
            )}
          </h1>
          <p className="text-base sm:text-lg leading-relaxed text-white/80 max-w-xl mb-10">
            {lang === "ar"
              ? "عضوية واحدة. وصول فوري لأرقى العيادات والمستشفيات في مصر."
              : "One membership. Instant access to Egypt's leading clinics and hospitals."}
          </p>
          {/* Stats */}
          <div className="flex items-center gap-10">
            <StatBadge value={`${stats.active_offers}+`} label={lang === "ar" ? "عرض نشط" : "Active Deals"} white />
            <StatBadge value={`${stats.providers}+`} label={lang === "ar" ? "مقدم خدمة" : "Providers"} white />
            <StatBadge value={`${stats.specialties}+`} label={lang === "ar" ? "تخصص" : "Specialties"} white />
          </div>
        </div>

        {/* Search bar — anchored to bottom, overlaps next section */}
        <div className="absolute bottom-0 left-0 right-0 translate-y-1/2 z-10 px-4 sm:px-6">
          <div className="max-w-5xl mx-auto bg-white rounded-2xl shadow-2xl border border-border/30 p-4 sm:p-5" data-testid="search-cta">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <Select value={specialty} onValueChange={setSpecialty}>
                <SelectTrigger className="h-12 border-border/60" data-testid="search-specialty">
                  <SelectValue placeholder={lang === "ar" ? "التخصص" : "Specialty"} />
                </SelectTrigger>
                <SelectContent>
                  {categories.map(cat => (
                    <SelectItem key={cat.value} value={cat.value}>
                      {lang === "ar" ? cat.label_ar : cat.label_en}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={governorate} onValueChange={(v) => { setGovernorate(v); setCity(""); }}>
                <SelectTrigger className="h-12 border-border/60" data-testid="search-governorate">
                  <SelectValue placeholder={lang === "ar" ? "المحافظة" : "Governorate"} />
                </SelectTrigger>
                <SelectContent>
                  {governorates.map(g => (
                    <SelectItem key={g.value} value={g.value}>
                      {lang === "ar" ? g.label_ar : g.label_en}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={city} onValueChange={setCity} disabled={!governorate}>
                <SelectTrigger className="h-12 border-border/60" data-testid="search-city">
                  <SelectValue placeholder={lang === "ar" ? "المنطقة" : "City / Area"} />
                </SelectTrigger>
                <SelectContent>
                  {neighborhoods.map(n => (
                    <SelectItem key={n.value} value={n.value}>
                      {lang === "ar" ? n.label_ar : n.label_en}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Button
                onClick={handleSearch}
                className="h-12 bg-primary hover:bg-primary/90 text-white font-semibold text-base rounded-xl"
                data-testid="search-cta-button"
              >
                <Search className="h-5 w-5 me-2" />
                {lang === "ar" ? "ابحث الآن" : "Find Deals"}
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="pt-72 sm:pt-48 lg:pt-36 pb-24 bg-white" data-testid="how-it-works-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary mb-3">
              {lang === "ar" ? "كيف يعمل" : "How It Works"}
            </p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-foreground">
              {lang === "ar" ? "ثلاث خطوات بسيطة" : "Three Simple Steps"}
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <StepCard
              num="01"
              title={lang === "ar" ? "اشترك" : "Join Hemaia"}
              desc={lang === "ar" ? "سجل حسابك واحصل على بطاقة عضوية رقمية فوراً" : "Sign up and get your digital membership card instantly"}
              icon={<CreditCard className="h-7 w-7" />}
            />
            <StepCard
              num="02"
              title={lang === "ar" ? "ابحث عن طبيبك" : "Find Your Doctor"}
              desc={lang === "ar" ? "تصفح مئات الأطباء والعيادات حسب التخصص والمنطقة" : "Browse hundreds of doctors and clinics by specialty and location"}
              icon={<Search className="h-7 w-7" />}
            />
            <StepCard
              num="03"
              title={lang === "ar" ? "وفّر مع كل زيارة" : "Save Every Visit"}
              desc={lang === "ar" ? "اعرض بطاقتك واحصل على خصومات حصرية فوراً" : "Show your card and get exclusive discounts applied instantly"}
              icon={<Heart className="h-7 w-7" />}
            />
          </div>
        </div>
      </section>

      {/* Browse by Category */}
      <section className="py-24" data-testid="categories-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary mb-3">
              {lang === "ar" ? "تخصصاتنا" : "Our Specialties"}
            </p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-foreground">
              {lang === "ar" ? "تصفح حسب التخصص" : "Browse by Specialty"}
            </h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {(data?.categories || []).slice(0, 8).map(cat => {
              const Icon = getCategoryIcon(cat.name_en);
              return (
                <button
                  key={cat.id}
                  onClick={() => { setSpecialty(cat.name_en); handleSearch(); }}
                  className="group flex flex-col items-center gap-3 p-6 rounded-2xl bg-white border border-border/50 hover:border-primary/30 hover:bg-secondary/20 hover:-translate-y-1 transition-all duration-200"
                  data-testid={`category-${cat.id}`}
                >
                  <div className="h-14 w-14 rounded-xl bg-secondary flex items-center justify-center text-primary group-hover:bg-primary group-hover:text-white transition-colors">
                    <Icon className="h-6 w-6" />
                  </div>
                  <span className="text-sm font-semibold text-foreground text-center">
                    {lang === "ar" ? cat.name_ar : cat.name_en}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {cat.children_count} {lang === "ar" ? "تخصص فرعي" : "subspecialties"}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Featured Deals */}
      <section className="py-24 bg-white" data-testid="deals-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary mb-3">
              {lang === "ar" ? "افضل العروض" : "Featured Deals"}
            </p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-foreground">
              {lang === "ar" ? "عروض مميزة مختارة لك" : "Highlighted for You"}
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {(data?.featured_deals || []).slice(0, 6).map((deal, i) => (
              <DealCard key={deal.id} deal={deal} lang={lang} index={i} onClick={() => navigate(`/deal/${deal.id}`)} />
            ))}
          </div>
          <div className="text-center mt-10">
            <Button
              onClick={() => navigate("/search")}
              variant="outline"
              className="rounded-xl px-6"
              data-testid="view-all-deals-btn"
            >
              {lang === "ar" ? "عرض جميع العروض" : "View All Deals"}
              <ChevronRight className="h-4 w-4 ms-1" />
            </Button>
          </div>
        </div>
      </section>

      {/* Latest Offers — recently added by providers */}
      {(data?.latest_offers || []).length > 0 && (
        <section className="py-24" data-testid="latest-offers-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary mb-3 inline-flex items-center gap-1.5">
                <Clock className="h-3.5 w-3.5" />
                {lang === "ar" ? "أحدث العروض" : "Just Added"}
              </p>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-foreground">
                {lang === "ar" ? "أحدث ما وصل إلى الشبكة" : "Latest Offers"}
              </h2>
              <p className="mt-3 text-sm text-muted-foreground max-w-md mx-auto">
                {lang === "ar"
                  ? "عروض جديدة من شبكتنا — أضافها مقدمو الخدمة هذا الأسبوع."
                  : "Fresh deals added by our providers this week."}
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {(data?.latest_offers || []).slice(0, 6).map((deal, i) => (
                <DealCard key={deal.id} deal={deal} lang={lang} index={i + 3} onClick={() => navigate(`/deal/${deal.id}`)} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Top Providers — partner clinics with active offers */}
      {(data?.providers || []).length > 0 && (
        <section className="py-24 bg-white" data-testid="top-providers-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary mb-3 inline-flex items-center gap-1.5">
                <Building2 className="h-3.5 w-3.5" />
                {lang === "ar" ? "أبرز مقدمي الخدمة" : "Featured Providers"}
              </p>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-foreground">
                {lang === "ar" ? "تعرّف على شركائنا" : "Trusted Healthcare Partners"}
              </h2>
              <p className="mt-3 text-sm text-muted-foreground max-w-md mx-auto">
                {lang === "ar"
                  ? "عيادات ومستشفيات وصيدليات معتمدة في شبكة حماية."
                  : "Verified clinics, hospitals and pharmacies in the Hemaia network."}
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {(data?.providers || []).slice(0, 6).map((p) => (
                <ProviderCard key={p.id} provider={p} lang={lang} onClick={() => navigate(`/providers/${p.id}`)} />
              ))}
            </div>
            <div className="text-center mt-10">
              <Button
                onClick={() => navigate("/search")}
                variant="outline"
                className="rounded-xl px-6"
              >
                {lang === "ar" ? "تصفح الشبكة بالكامل" : "Browse the Full Network"}
                <ChevronRight className="h-4 w-4 ms-1" />
              </Button>
            </div>
          </div>
        </section>
      )}

      {/* Benefits Section */}
      <section className="py-24" data-testid="benefits-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary mb-3">
              {lang === "ar" ? "لماذا حماية؟" : "Why Hemaia?"}
            </p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-foreground">
              {lang === "ar" ? "مميزات العضوية" : "Membership Benefits"}
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <BenefitCard
              icon={<Shield className="h-7 w-7" />}
              title={lang === "ar" ? "مقدمو خدمة موثوقون" : "Verified Providers"}
              desc={lang === "ar" ? "جميع الأطباء والعيادات حاصلون على تصاريح وزارة الصحة" : "All doctors and clinics are verified with Ministry of Health permits"}
            />
            <BenefitCard
              icon={<CreditCard className="h-7 w-7" />}
              title={lang === "ar" ? "يتطبق فورًا" : "Applied Instantly"}
              desc={lang === "ar" ? "اعرض عضويتك الرقمية، والسعر المميز بتاعك بيتطبق في نفس اللحظة" : "Present your digital membership and your preferred rate is applied on the spot"}
            />
            <BenefitCard
              icon={<Users className="h-7 w-7" />}
              title={lang === "ar" ? "تغطية عائلية" : "Family Coverage"}
              desc={lang === "ar" ? "أضف أفراد عائلتك للاستفادة من نفس المزايا" : "Add family members to enjoy the same benefits under one plan"}
            />
            <BenefitCard
              icon={<MapPin className="h-7 w-7" />}
              title={lang === "ar" ? "تغطية مصر بالكامل" : "Nationwide Coverage"}
              desc={lang === "ar" ? "شبكة متنامية في جميع المحافظات المصرية" : "Growing network across all Egyptian governorates"}
            />
            <BenefitCard
              icon={<Star className="h-7 w-7" />}
              title={lang === "ar" ? "عروض حصرية" : "Exclusive Deals"}
              desc={lang === "ar" ? "عروض خاصة للأعضاء فقط من أفضل مقدمي الخدمات" : "Member-only offers from top healthcare providers"}
            />
            <BenefitCard
              icon={<Activity className="h-7 w-7" />}
              title={lang === "ar" ? "سهل الاستخدام" : "Easy to Use"}
              desc={lang === "ar" ? "ابحث واحجز واحصل على خصمك — كل شيء من هاتفك" : "Search, visit, and save — all from your phone"}
            />
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 bg-white" data-testid="faq-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary mb-3">
              {lang === "ar" ? "أسئلة شائعة" : "FAQ"}
            </p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-foreground">
              {lang === "ar" ? "إجابات لأسئلتك" : "Answers to Your Questions"}
            </h2>
          </div>
          <Accordion type="single" collapsible className="space-y-3">
            {getFAQs(lang).map((faq, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="border border-border/60 rounded-xl px-6 data-[state=open]:bg-secondary/20">
                <AccordionTrigger className="text-start text-sm sm:text-base font-semibold hover:no-underline">
                  {faq.q}
                </AccordionTrigger>
                <AccordionContent className="text-sm text-muted-foreground leading-relaxed">
                  {faq.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Membership CTA — card + signup form */}
      <CTAMembershipSection lang={lang} isRTL={isRTL} />

      {/* Footer */}
      <Footer />
    </div>
  );
}

/* ─── Sub-components ─── */

function StatBadge({ value, label, white }) {
  return (
    <div className="text-center">
      <p className={`text-2xl sm:text-3xl font-black ${white ? "text-white" : "text-foreground"}`}>{value}</p>
      <p className={`text-xs font-medium ${white ? "text-white/70" : "text-muted-foreground"}`}>{label}</p>
    </div>
  );
}

function StepCard({ num, title, desc, icon }) {
  return (
    <div className="relative p-8 rounded-2xl border border-border/50 bg-background hover:border-primary/30 transition-colors group">
      <span className="absolute top-4 end-4 text-5xl font-black text-secondary select-none">{num}</span>
      <div className="h-14 w-14 rounded-xl bg-secondary flex items-center justify-center text-primary mb-5 group-hover:bg-primary group-hover:text-white transition-colors">
        {icon}
      </div>
      <h3 className="text-lg font-semibold text-foreground mb-2">{title}</h3>
      <p className="text-sm text-muted-foreground leading-relaxed">{desc}</p>
    </div>
  );
}

function ProviderCard({ provider, lang, onClick }) {
  const typeLabels = {
    CLINIC: lang === "ar" ? "عيادة" : "Clinic",
    HOSPITAL: lang === "ar" ? "مستشفى" : "Hospital",
    PHARMACY: lang === "ar" ? "صيدلية" : "Pharmacy",
    LAB: lang === "ar" ? "معمل" : "Laboratory",
    RADIOLOGY_CENTER: lang === "ar" ? "مركز أشعة" : "Radiology",
    OTHER: lang === "ar" ? "أخرى" : "Other",
  };
  const Wrapper = onClick ? "button" : "div";

  return (
    <Wrapper
      type={onClick ? "button" : undefined}
      onClick={onClick}
      className={`text-start w-full rounded-2xl border border-border/50 bg-white overflow-hidden hover:shadow-md hover:-translate-y-1 transition-all duration-200 ${onClick ? "cursor-pointer group" : ""}`}
      data-testid={`provider-card-${provider.id}`}
    >
      <div className="h-40 bg-gradient-to-br from-secondary via-secondary/50 to-primary/10 flex items-center justify-center">
        {provider.logo_url ? (
          <img src={provider.logo_url} alt="" className="h-20 w-20 object-contain rounded-xl" />
        ) : (
          <Building2 className="h-12 w-12 text-primary/40" />
        )}
      </div>
      <div className="p-5">
        <div className="flex items-start justify-between gap-2 mb-2">
          <h3 className="text-base font-semibold text-foreground line-clamp-1 group-hover:text-primary transition-colors">{provider.corporate_name}</h3>
          <span className="text-[11px] font-medium text-primary bg-secondary px-2 py-0.5 rounded-full whitespace-nowrap">
            {typeLabels[provider.type] || provider.type}
          </span>
        </div>
        <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
          {lang === "ar" ? provider.description_ar : provider.description_en}
        </p>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <MapPin className="h-3.5 w-3.5" />
            <span className="line-clamp-1">{(provider.neighborhoods || []).join(", ")}</span>
          </div>
          {provider.offer_count > 0 && (
            <span className="text-[11px] font-semibold text-white bg-primary px-2 py-0.5 rounded-full">
              {provider.offer_count} {lang === "ar" ? "عرض" : "deal(s)"}
            </span>
          )}
        </div>
      </div>
    </Wrapper>
  );
}

function BenefitCard({ icon, title, desc }) {
  return (
    <div className="p-6 rounded-2xl border border-border/50 bg-white hover:border-primary/30 transition-colors">
      <div className="h-12 w-12 rounded-xl bg-secondary flex items-center justify-center text-primary mb-4">
        {icon}
      </div>
      <h3 className="text-base font-semibold text-foreground mb-2">{title}</h3>
      <p className="text-sm text-muted-foreground leading-relaxed">{desc}</p>
    </div>
  );
}

function DealCard({ deal, lang, index = 0, onClick }) {
  const discountedPrice = deal.original_price
    ? Math.round(deal.original_price * (1 - deal.discount_value / 100))
    : null;

  const DEAL_IMAGES = [
    "https://images.pexels.com/photos/3845810/pexels-photo-3845810.jpeg?auto=compress&cs=tinysrgb&w=400&h=300&fit=crop",
    "https://images.pexels.com/photos/4226219/pexels-photo-4226219.jpeg?auto=compress&cs=tinysrgb&w=400&h=300&fit=crop",
    "https://images.pexels.com/photos/5215024/pexels-photo-5215024.jpeg?auto=compress&cs=tinysrgb&w=400&h=300&fit=crop",
    "https://images.pexels.com/photos/4386467/pexels-photo-4386467.jpeg?auto=compress&cs=tinysrgb&w=400&h=300&fit=crop",
    "https://images.pexels.com/photos/4225880/pexels-photo-4225880.jpeg?auto=compress&cs=tinysrgb&w=400&h=300&fit=crop",
    "https://images.pexels.com/photos/3938022/pexels-photo-3938022.jpeg?auto=compress&cs=tinysrgb&w=400&h=300&fit=crop",
  ];
  const imgSrc = deal.image_url || DEAL_IMAGES[index % DEAL_IMAGES.length];

  return (
    <div
      className="rounded-2xl border border-border/50 bg-white overflow-hidden hover:shadow-lg hover:-translate-y-1 transition-all duration-200 cursor-pointer group"
      data-testid={`deal-card-${deal.id}`}
      onClick={onClick}
    >
      <div className="relative h-44 overflow-hidden">
        <img
          src={imgSrc}
          alt={deal.title_en}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
        <div className="absolute top-3 end-3 bg-primary text-white text-sm font-bold px-3 py-1 rounded-full flex items-center gap-1 shadow-md">
          <Percent className="h-3.5 w-3.5" />
          {deal.discount_value}% {lang === "ar" ? "خصم" : "OFF"}
        </div>
        <div className="absolute bottom-3 start-3 flex items-center gap-2">
          <div className="h-8 w-8 rounded-lg bg-white/90 flex items-center justify-center">
            {deal.provider_logo ? (
              <img src={deal.provider_logo} alt="" className="h-5 w-5 object-contain rounded" />
            ) : (
              <Building2 className="h-4 w-4 text-primary/60" />
            )}
          </div>
          <span className="text-xs font-medium text-white drop-shadow-md">
            {deal.provider_name}
          </span>
        </div>
      </div>
      <div className="p-4">
        <h3 className="text-base font-semibold text-foreground leading-snug line-clamp-2 mb-3 group-hover:text-primary transition-colors">
          {lang === "ar" ? deal.title_ar : deal.title_en}
        </h3>
        {deal.original_price && (
          <div className="flex items-baseline gap-2 mb-3">
            <span className="text-xl font-bold text-primary">
              {discountedPrice} {lang === "ar" ? "ج.م" : "EGP"}
            </span>
            <span className="text-sm text-muted-foreground line-through">
              {deal.original_price}
            </span>
          </div>
        )}
        <div className="flex items-center justify-between text-xs text-muted-foreground pt-2 border-t border-border/30">
          <div className="flex items-center gap-1">
            <MapPin className="h-3 w-3" />
            <span className="truncate max-w-[100px]">
              {(deal.provider_neighborhoods || []).slice(0, 2).join(", ")}
            </span>
          </div>
          <div className="flex items-center gap-1">
            <Clock className="h-3 w-3" />
            <span>{lang === "ar" ? "حتى" : "Until"} {deal.end_date?.slice(5)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}


function PhysicalCardPreview({ lang }) {
  return (
    <div
      className="relative w-full overflow-hidden rounded-2xl shadow-2xl"
      style={{
        aspectRatio: "85.6/53.98",
        background: "linear-gradient(135deg, #003F9E 0%, #0060CC 45%, #0088EE 100%)",
        maxWidth: "460px",
      }}
    >
      {/* Hexagonal grid pattern */}
      <svg className="absolute inset-0 w-full h-full" xmlns="http://www.w3.org/2000/svg" style={{ opacity: 0.07 }}>
        <defs>
          <pattern id="hexCardPreview" width="30" height="52" patternUnits="userSpaceOnUse">
            <path d="M15 1 L29 9 L29 25 L15 33 L1 25 L1 9 Z" fill="none" stroke="white" strokeWidth="1" />
            <path d="M15 33 L29 41 M15 33 L1 41" fill="none" stroke="white" strokeWidth="1" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#hexCardPreview)" />
      </svg>

      {/* ECG heartbeat line */}
      <svg
        className="absolute top-0 h-full"
        style={{ right: 0, width: "58%" }}
        viewBox="0 0 220 150"
        preserveAspectRatio="xMidYMid meet"
      >
        <path
          d="M0,75 L25,75 L33,68 L40,75 L46,52 L56,100 L66,75 L90,75 L98,68 L105,75 L111,52 L121,100 L131,75 L155,75 L163,68 L170,75 L176,52 L186,100 L196,75 L220,75"
          fill="none" stroke="#38B6FF" strokeWidth="6" opacity="0.3"
        />
        <path
          d="M0,75 L25,75 L33,68 L40,75 L46,52 L56,100 L66,75 L90,75 L98,68 L105,75 L111,52 L121,100 L131,75 L155,75 L163,68 L170,75 L176,52 L186,100 L196,75 L220,75"
          fill="none" stroke="#5DCCFF" strokeWidth="2"
        />
      </svg>

      {/* Hemaia logo — top right */}
      <div className="absolute top-4 right-5">
        <img src="/hemaia_logo_light.svg" alt="Hemaia" className="h-6 sm:h-7" />
      </div>

      {/* Card content */}
      <div className="absolute inset-0 p-4 sm:p-5 flex flex-col justify-between">
        <div />
        <div>
          <p className="text-white/50 text-[7px] sm:text-[8px] uppercase tracking-[0.15em] font-medium mb-0.5">
            {lang === "ar" ? "رقم البطاقة" : "Card Number"}
          </p>
          <p className="text-white font-mono text-sm sm:text-base tracking-[0.22em] font-bold">
            2345 8762 1259
          </p>
        </div>
        <div className="flex items-end justify-between">
          <div className="space-y-2">
            <div>
              <p className="text-white/50 text-[7px] sm:text-[8px] uppercase tracking-[0.1em] font-medium mb-0.5">
                {lang === "ar" ? "صالحة حتى" : "Valid Thru"}
              </p>
              <p className="text-white text-xs sm:text-sm font-semibold">01/05/2027</p>
            </div>
            <div>
              <p className="text-white/50 text-[7px] sm:text-[8px] uppercase tracking-[0.1em] font-medium mb-0.5">
                {lang === "ar" ? "اسم حامل البطاقة" : "Card Holder Name"}
              </p>
              <p className="text-white text-xs sm:text-sm font-bold uppercase tracking-wide">
                {lang === "ar" ? "محمد أحمد" : "Your Name Here"}
              </p>
            </div>
          </div>
          <div className="text-right pb-0.5">
            <p className="text-white/65 text-[7px] sm:text-[8px] font-bold uppercase leading-tight tracking-wide">
              {lang === "ar" ? <>شبكة الولاء<br />الصحية في مصر</> : <>Egypt's Healthcare<br />Loyalty Network</>}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function CTAMembershipSection({ lang, isRTL }) {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");

  const handleSubmit = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (name) params.set("name", name);
    if (email) params.set("email", email);
    if (phone) params.set("phone", phone);
    navigate(`/signup?${params.toString()}`);
  };

  return (
    <section className="py-24 bg-gradient-to-b from-secondary/30 via-background to-background" data-testid="membership-cta-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">

          {/* Left — card preview + copy */}
          <div className="space-y-8 order-2 lg:order-1">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary mb-3">
                {lang === "ar" ? "ابدأ رحلتك" : "Start Your Journey"}
              </p>
              <h2 className="text-3xl sm:text-4xl font-black tracking-tight text-foreground mb-4">
                {lang === "ar"
                  ? <><span className="text-primary">صحتك.</span><br />بشروطك.</>
                  : <>Your Health.<br /><span className="text-primary">Your Terms.</span></>}
              </h2>
              <p className="text-base text-muted-foreground leading-relaxed max-w-lg">
                {lang === "ar"
                  ? "وفّر من 20% إلى 60% على الاستشارات والأسنان والتشخيص وأكثر، عبر أكبر شبكة ولاء صحي في مصر. عضوية واحدة. توفير لا محدود."
                  : "Save 20–60% on consultations, dental, diagnostics, and more across Egypt's largest healthcare loyalty network. One membership. Unlimited savings."}
              </p>
            </div>

            {/* Stats */}
            <div className="flex flex-wrap gap-8">
              {[
                { v: "500+", l: lang === "ar" ? "مقدم خدمة" : "Providers" },
                { v: "40%",  l: lang === "ar" ? "متوسط التوفير" : "Avg. Savings" },
                { v: "12+",  l: lang === "ar" ? "تخصص طبي" : "Specialties" },
              ].map(({ v, l }) => (
                <div key={l}>
                  <p className="text-2xl font-black text-foreground">{v}</p>
                  <p className="text-xs text-muted-foreground font-medium">{l}</p>
                </div>
              ))}
            </div>

            <PhysicalCardPreview lang={lang} />
          </div>

          {/* Right — signup form */}
          <div className="order-1 lg:order-2">
            <div className="bg-white rounded-3xl border border-border/50 shadow-xl p-8 sm:p-10">
              <div className="mb-6">
                <h3 className="text-xl font-bold text-foreground mb-1">
                  {lang === "ar" ? "أنشئ حسابك" : "Create Your Account"}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {lang === "ar"
                    ? "انضم لآلاف الأعضاء الذين يوفرون في كل زيارة طبية."
                    : "Join thousands of members saving on every medical visit."}
                </p>
              </div>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-foreground mb-1.5">
                    {lang === "ar" ? "الاسم الكامل" : "Full Name"}
                  </label>
                  <Input
                    value={name}
                    onChange={e => setName(e.target.value)}
                    placeholder={lang === "ar" ? "محمد أحمد" : "John Smith"}
                    className="h-11"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-foreground mb-1.5">
                    {lang === "ar" ? "البريد الإلكتروني" : "Email Address"}
                  </label>
                  <Input
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="name@example.com"
                    className="h-11"
                    dir="ltr"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-foreground mb-1.5">
                    {lang === "ar" ? "رقم الهاتف" : "Phone Number"}
                  </label>
                  <Input
                    type="tel"
                    value={phone}
                    onChange={e => setPhone(e.target.value)}
                    placeholder="010xxxxxxxx"
                    className="h-11"
                    dir="ltr"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full h-12 text-base font-semibold rounded-xl bg-primary hover:bg-primary/90"
                >
                  {lang === "ar" ? "انضم الآن" : "Join Now"}
                </Button>
                <p className="text-center text-xs text-muted-foreground">
                  {lang === "ar"
                    ? "500 جنيه / سنة · بدون رسوم مخفية · وصول فوري"
                    : "500 EGP/year · No hidden fees · Instant access"}
                </p>
              </form>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}

function getFAQs(lang) {
  if (lang === "ar") {
    return [
      { q: "هل يوجد حد لعدد المرات التي يمكنني فيها الاستفادة من العروض؟", a: "لا يوجد حد. تمنحك العضوية السنوية إمكانية استخدام الخصومات بشكل متكرر طوال فترة الاشتراك." },
      { q: "كم تكلفة الاشتراك في حماية؟", a: "التسعير بسيط وواضح: 500 جنيه سنوياً — رسوم واحدة، بدون أي رسوم مخفية أو مفاجآت." },
      { q: "هل يمكنني استخدام حماية فوراً بعد الاشتراك؟", a: "نعم، فوراً. بمجرد تأكيد الدفع يتم تفعيل عضويتك مباشرة ويمكنك الحجز والتوفير على الفور." },
      { q: "ما هي الخدمات المشمولة في العضوية؟", a: "تشمل الخصومات: الاستشارات الطبية، خدمات الأسنان، التحاليل والأشعة، خدمات البصريات، العلاج الطبيعي، خدمات العناية الصحية، باقات الأمومة، وخدمات الطوارئ." },
      { q: "هل أدفع للعيادة مباشرة؟", a: "نعم، دائماً. تدفع مباشرة للعيادة أو المستشفى بالسعر المخفض. لا وسطاء، لا انتظار للتعويض." },
      { q: "ما الفرق بين حماية والتأمين الصحي؟", a: "التأمين يغطي حالات محددة ويتطلب موافقات ومطالبات معقدة. حماية تمنحك خصومات فورية بدون أوراق أو إجراءات — تستخدمها مباشرة وفي كل مرة." },
      { q: "هل العيادات الشريكة موثوقة؟", a: "نعم، بدون استثناء. نتعامل فقط مع عيادات ومستشفيات مرخصة وملتزمة باللوائح الصحية المصرية. كل مقدم خدمة يخضع للتحقق قبل الانضمام." },
    ];
  }
  return [
    { q: "Is there a limit to how many times I can access deals?", a: "No artificial limits. Your membership gives you ongoing, repeat access to discounted healthcare throughout your entire subscription period." },
    { q: "How much does Hemaia cost?", a: "Simple, transparent pricing: 500 EGP/year — one flat fee, no hidden charges, no surprise bills." },
    { q: "Can I use Hemaia immediately after subscribing?", a: "Yes — instantly. The moment your payment is confirmed, your membership is activated and you can start booking and saving immediately." },
    { q: "What services are included?", a: "Discounts apply across general consultations, dental care, lab tests, radiology, optical services, physiotherapy, wellness treatments, maternity packages, and emergency services." },
    { q: "Do I pay the clinic directly?", a: "Yes, always. You pay the provider on-site at the discounted rate. No intermediaries, no reimbursement delays." },
    { q: "What is the difference between Hemaia and insurance?", a: "Insurance covers specific risks and requires approvals and complex claims. Hemaia gives you instant discounts — no approvals, no paperwork. It works immediately, every time." },
    { q: "Are partner clinics verified?", a: "Yes, without exception. We work only with licensed clinics and hospitals, fully compliant with Egypt healthcare regulations. Every provider is vetted before joining our network." },
  ];
}
